import { useLoaderData } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import {
  MANOS_SUPPORT_EMAIL,
  MANOS_LEGAL_ENTITY,
  MANOS_COMPANY_NUMBER,
  MANOS_REGISTERED_OFFICE,
} from "../../config/branding";
import { getVisitorGeo } from "@barrykilby/geo";
import { ALLOW_AS_COUNTRIES } from "../root";
import { promoMonthlyDisplay, entryMonthlyDisplay } from "@/lib/plans";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const geo = getVisitorGeo(request, { allowAsOverride: ALLOW_AS_COUNTRIES });
  return {
    promoPrice: promoMonthlyDisplay(geo.country),
    soloPrice: entryMonthlyDisplay("solo", geo.country),
    teamPrice: entryMonthlyDisplay("team", geo.country),
  };
};

export default function Terms() {
  const { promoPrice, soloPrice, teamPrice } = useLoaderData<typeof loader>();
  return (
    <div className="mx-auto max-w-3xl px-6 py-16 space-y-8">
      <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
      <p className="text-sm text-muted-foreground">Last updated: 8 May 2026</p>

      <div className="prose prose-stone dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed text-muted-foreground">
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Who you're contracting with</h2>
          <p>
            These terms are an agreement between you and {MANOS_LEGAL_ENTITY}, a company
            registered in England and Wales (company number {MANOS_COMPANY_NUMBER})
            with its registered office at {MANOS_REGISTERED_OFFICE}. References to
            "we", "us", and "Manos" mean {MANOS_LEGAL_ENTITY}.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">The service</h2>
          <p>Manos is a business management tool for service professionals. We provide job tracking, client management, invoicing, scheduling, and reporting features via our web and mobile applications.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Your account</h2>
          <p>You are responsible for maintaining the security of your account. You must provide accurate information when creating your account. You must not share your account with others.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Pricing</h2>
          <p>Manos starts with 3 days free, then {promoPrice} for 3 months, then your chosen plan price (Solo {soloPrice}, Team {teamPrice}, Enterprise by arrangement). No annual contract -- cancel anytime. A small application fee applies to card payments processed through Stripe (1% Solo, 0.7% Team, negotiated Enterprise). Cash and bank transfer payments incur no commission.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Your data</h2>
          <p>You own your data. We do not claim any intellectual property rights over the content you create in Manos. You can export your data at any time. If you cancel your account, we will delete your data within 30 days.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Availability</h2>
          <p>We aim for 99.9% uptime but do not guarantee it. We may perform maintenance with advance notice. We are not liable for losses caused by service interruptions.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Acceptable use</h2>
          <p>You must not use Manos for illegal purposes, to store illegal content, or to harass others. We reserve the right to terminate accounts that violate these terms.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Changes</h2>
          <p>We may update these terms. We will notify you of significant changes via email. Continued use after changes constitutes acceptance.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Contact</h2>
          <p>
            Questions? Send us a message via our <a href="/contact?topic=terms" className="text-amber-600 hover:underline">contact form</a>{" "}
            or write to {MANOS_LEGAL_ENTITY}, {MANOS_REGISTERED_OFFICE}.
          </p>
        </section>
      </div>
    </div>
  );
}
